import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { getTextSize, setTextSize, TEXT_SIZES } from "../utils/textsize";

const sizeBtnSx = { minHeight: "var(--hit-min)", px: 2, fontWeight: 600, whiteSpace: "nowrap" };

/* 深底上的切換鈕：框線與字改成淺色，選中的那顆用半透明白底標出來 */
const FOOTER_TOGGLE_SX = {
  "& .MuiToggleButton-root": {
    color: "var(--ink-on-footer-muted)",
    borderColor: "rgba(255, 255, 255, 0.28)",
    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
    "&.Mui-selected": { color: "var(--ink-on-footer)", bgcolor: "rgba(255, 255, 255, 0.16)" },
    "&.Mui-selected:hover": { bgcolor: "rgba(255, 255, 255, 0.22)" },
    "&.Mui-focusVisible": { outline: "var(--focus-w) solid var(--ink-on-footer)", outlineOffset: 2 },
  },
};

/**
 * 登入版型 — 滿版容器，中間放登入卡，最底下一條深灰黑的 footer（版權＋字級切換）。
 *
 * 不再自己讀一次 getTheme() 掛 data-theme：佈景只掛在 <html>，
 * index.js 進站時就 initTheme() 過了，登入頁一樣吃得到同一組 token。
 *
 * 字級切換放在登入前的頁面是有意的：還沒登入就能先調，utils/textsize.js 寫的是
 * localStorage，不需要帳號也存得起來，之後每一次登入都會記得。
 * 登入、忘記密碼、重設密碼三頁共用這個版型，所以 footer 只寫這一份。
 */
function App() {
  const [size, setSize] = useState(() => getTextSize());
  /* 立刻套用並寫回 localStorage；回傳值就是正規化後的值，直接當新的 state */
  const pickSize = (v) => setSize(setTextSize(v));

  return (
    /* overflow:auto —— 容器是 fixed 的，大字模式下登入卡會比視窗高，
       沒有捲動的話下半截（包含送出鈕）就永遠點不到 */
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        color: "text.primary",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--t-body)",
        lineHeight: "var(--t-body-lh)",
        WebkitFontSmoothing: "antialiased",
      }}
    >
      {/* 置中容器。內距走主題間距，不寫死 px，大字模式才跟得上 */}
      <Box
        component="main"
        sx={{
          flex: "1 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          boxSizing: "border-box",
        }}
      >
        <Outlet />
      </Box>

      <Box
        component="footer"
        sx={{
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, md: 4 },
          py: 2,
          bgcolor: "var(--surface-footer)",
          color: "var(--ink-on-footer)",
        }}
      >
        <Typography variant="body2" sx={{ color: "var(--ink-on-footer-muted)" }}>
          © 2026 易磊科技 EL Tech · EL PM
        </Typography>
        {/*
          三顆鈕刻意用不同字級（15／17／21px）：這是「小看起來小、大看起來大」的預覽，
          不能跟著 --t-* 一起變，否則切換之後三顆就分不出來。
          值 "" 是「中」（標準）；MUI 的 exclusive 群組再按一次已選的會回 null，所以擋掉 null。
        */}
        <ToggleButtonGroup
          exclusive
          value={size}
          onChange={(_e, v) => {
            if (v !== null) pickSize(v);
          }}
          aria-label="字級"
          sx={FOOTER_TOGGLE_SX}
        >
          {TEXT_SIZES.map((o) => (
            <ToggleButton key={o.label} value={o.value} aria-label={`字級：${o.label}`} sx={{ ...sizeBtnSx, fontSize: o.preview }}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

export default App;
